<template>
  <div class="policy-dashboard container f-rte">
    <Tabs v-model="selectedTab">
      <Tab title="By policy area">
        <ControlGroup>
          <Dropdown
            label="Policy area"
            :options="topics"
            v-model="topic"
            fixed
          />
          <ScorecardFilter
            key="familySCfilter"
            label="Policy type"
            :selectedOption="family"
            :options="familiesByTopic"
            @changed="(value) => (family = value)"
          />
        </ControlGroup>
        <DataGetter
          :url="url"
          :key="`${topic}${family}`"
          :params="{ topic, jurisdiction: ['National','International'] }"
          @fetched="setPolicies"
          lazy
        >
          <ScoreCards
            :key="`${topic}${family}${selectedTab}`"
            ref="scorecards"
            :url="url"
            :family="family"
            :topic="topic"
            :policies="policiesByFamily"
            :setRegion="setRegion"
            :test="test"
          />
        </DataGetter>
      </Tab>
      <Tab title="By region">
        <ControlGroup>
          <Dropdown
              label="Country/region"
              :options="regions"
              v-model="country"
              fixed
            />
        </ControlGroup>
        <ControlGroup>
          <Dropdown
            label="Policy area"
            :options="topics"
            v-model="topic"
            fixed
          />
          <ScorecardFilter
            key="countrySCfilter"
            label="Policy type"
            :selectedOption="family"
            :options="['All', ...relevantFamilies]"
            @changed="
              selectedTab === 'By region' ? (value) => (family = value) : null
            "
          />
        </ControlGroup>
        <DataGetter
          :url="url"
          :key="`${topic}${family}${country}`"
          :params="{ topic, country: isEU ? [country, 'European Union'] : country }"
          v-slot="{ data }"
          lazy
        >
          <ScoreLegend />
          <template v-if="family == 'All'">
            <CountryScorecard
              v-for="topicFamily in relevantFamilies"
              :key="`${topicFamily}countrySC`"
              :country="country"
              :topic="topic"
              :family="topicFamily"
              :policies="data"
              :test="test"
            />
            <p v-if="relevantFamilies.length === 0" class="f-ui-2">
              No relevant policies for this region.
            </p>
          </template>
          <CountryScorecard
            v-else
            :country="country"
            :topic="topic"
            :family="family"
            :policies="data"
            :test="test"
          />
        </DataGetter>
      </Tab>
    </Tabs>
  </div>
</template>

<script>
import ScoreCards from "./components/ScoreCards.vue";
import ScorecardFilter from "./components/ScorecardFilter.vue";
import ScoreLegend from "./components/ScoreLegend.vue";
import {
  Tabs,
  Tab,
  DataGetter,
  ControlGroup,
  Dropdown,
} from "@international-energy-agency/vue-ui";
import tags from "@/assets/tags.json";
import CountryScorecard from "./components/CountryScorecard.vue";
import g20 from '@/assets/g20.json';
import members from '@/assets/members.json';
import eu27 from '@/assets/eu27.json'

export default {
  name: "App",
  components: {
    ScorecardFilter,
    ScoreCards,
    CountryScorecard,
    ScoreLegend,
    DataGetter,
    ControlGroup,
    Dropdown,
    Tabs,
    Tab,
  },
  props: {
    test: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedTab: "By policy area",
      topic: "Buildings",
      family: "All",
      country: "France",
      regions: [...g20, ...members].sort(),
      policies: [],
      topics: tags
        .reduce(
          (topics, tag) =>
            topics.includes(tag.topic) ? topics : [...topics, tag.topic],
          []
        )
    };
  },
  computed: {
    url() {
      const { test } = this;
      return `https://${test ? "api.test" : "api"}.iea.org/policies/v2`;
    },
    policiesByFamily() {
      const { family } = this;
      return family === "All"
        ? this.policies
        : this.policies.filter((policy) => policy.type.includes(family));
    },
    familiesByTopic() {
      const { topic } = this;
      return tags.reduce(
        (families, tag) =>
          tag.topic === topic && tag.family && !families.includes(tag.family)
            ? [...families, tag.family]
            : families,
        ["All"]
      );
    },
    countryPolicies() {
      const { country } = this;
      return this.policies.filter((policy) => policy.country === country);
    },
    relevantFamilies() {
      const { countryPolicies, familiesByTopic } = this;
      return familiesByTopic.filter(
        (f) =>
          f !== "All" &&
          countryPolicies.some((policy) => policy.type.includes(f))
      );
    },
    isEU() {
      const { country } = this;
      return eu27.includes(country);
    }
  },
  methods: {
    handleTopic(item) {
      this.topic = item;
    },
    handlePolicyType(item) {
      this.policyType = item;
    },
    setPolicies(data) {
      this.policies = data;
    },
    setRegion(region) {
      this.country = region;
      this.selectedTab = "By region";
    },
    /* Used if we want to fetch topics/policytypes from API */
    getFamilies(data) {
      return data
        .reduce(
          (families, row) =>
            families.includes(row.family)
              ? families
              : [...families, row.family],
          []
        )
        .sort();
    },
  },
};
</script>

<style>
.policy-dashboard .breadcrumbs {
  color: #949494;
}
.policy-dashboard .score {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
}
.policy-dashboard .score-radius {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin: 0px 4px;
}
.policy-dashboard .score-radius.in-force,
.policy-dashboard .score-radius.achieved {
  background: #04f;
}
.policy-dashboard .score-radius.announced,
.policy-dashboard .score-radius.EU {
  border: 2px solid #04f;
}
.policy-dashboard .score-radius.achieved:before {
  display: block;
  width: 14px;
  height: 14px;
  content: "✓";
  color: #fff;
  font-weight: 600;
  font-size: 10px;
  text-align: center;
  line-height: 14px;
  position: relative;
}
.policy-dashboard .score-radius.ended:before {
  display: block;
  width: 14px;
  height: 14px;
  content: "-";
  color: #04f;
  font-size: 20px;
  text-align: center;
  line-height: 14px;
  position: relative;
}
.policy-dashboard .score-radius.EU:before {
  display: block;
  content: " ";
   width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #04f;
  position: relative;
  top: 2px;
  left: 2px;
}
.policy-dashboard a {
  box-shadow: none !important;
}
</style>
